<script setup lang="ts">
  // biome-ignore-all lint/suspicious/noExplicitAny: <intentionally any>
  import { Loader } from '@tundr/ds-components';
  import { semanticTokenVars } from '@tundr/ds-tokens';
  import { computed, ref, useAttrs, useSlots } from 'vue';
  import EasyDataTable, {
    DEFAULT_CHECKBOX_COLUMN_WIDTH,
  } from '../../internal-data-table';
  import { useFilters, useOnFiltersChange } from '../../composables';
  import { useColumnVisibility } from '../../composables/use-column-visibility.ts';
  import { useDataTableInternals } from '../../composables/use-data-table-internals.ts';
  import { useLastFixedColumnShadow } from '../../composables/use-last-fixed-column-shadow.ts';
  import type { OnChangeFiltersEvent } from '../../store';
  import {
    FilterTypes,
    hasCompactFilters,
    headerClassNameFunction,
    itemClassNameFunction,
    rowClassNameFunction,
  } from '../../utils';
  import DataTableSelection from '../PaginatedDataTable/DataTableSelection/DataTableSelection.vue';
  import { TableEmptyState } from '../TableEmptyState';
  import { TableTopBar } from '../filters';
  import {
    centeredContainerClass,
    dataTableContainerClass,
    tableContainerClass,
    tableFooterClass,
    tableHeaderClass,
    tableHeaderWrapperClass,
    tableHeaderWrapperSpacingClass,
    tableTitleClass,
    tableWithHeaderClass,
  } from './DataTable.css';
  import { type DataTableProps, SortType } from './DataTable.types.ts';

  const props = withDefaults(defineProps<DataTableProps>(), {
    loading: false,
    alternating: false,
    searchFieldName: FilterTypes.TEXT_SEARCH,
    borderCell: false,
    selectable: false,
    hideGeneralFilter: false,
    hideTextSearch: false,
    showIndex: false,
    hideSelectAll: false,
    fixedCheckbox: false,
    checkboxColumnWidth: DEFAULT_CHECKBOX_COLUMN_WIDTH,
    fixedIndex: false,
    indexColumnWidth: 36,
    sortBy: '',
    sortType: SortType.ASC,
    rowsPerPage: 100000,
    tableMinHeight: 400,
    autoHeight: false,
    hideableColumns: false,
    nonHideableColumns: () => [],
    /*
                      Left undefined on purpose: Vue would otherwise cast the absent boolean
                      to false and permanently opt the table out of the compact filter bar.
                    */
    compactFilters: undefined,
  });

  const attrs = useAttrs();
  const slots = useSlots();
  const filtersStore = useFilters(() => props.filterNamespace);

  const emits = defineEmits<{
    search: [searchText: string];
    filter: [OnChangeFiltersEvent];
    rowClick: [row: any];

    middleclickRow: [row: any];
  }>();

  const columnVisibility = useColumnVisibility(
    props.columnVisibilityNamespace ?? '',
    props.nonHideableColumns,
  );

  const visibleHeaders = computed(() => {
    if (!props.hideableColumns || !props.columnVisibilityNamespace)
      return props.headers;
    return columnVisibility.getVisibleColumns(props.headers);
  });

  const tableHasLastFixedColumn = computed(() => {
    return visibleHeaders.value.some((header) => header.lastFixed);
  });

  const tableContainerRef = ref<HTMLElement | null>(null);
  const { showLastFixedColumnShadow } =
    useLastFixedColumnShadow(tableContainerRef);

  const hasFooterSlot = computed(() => {
    return !!slots.footer;
  });

  const dataTableClasses = computed(() =>
    [
      'tundr-table',
      dataTableContainerClass,
      props.selectable && 'selectable',
      hasFooterSlot.value && 'with-footer',
      props.clickableRows && 'clickable',
      props.hideSelectAll && 'hide-all-selector',
      tableHasLastFixedColumn.value && 'table-with-last-fixed-column',
      tableHasLastFixedColumn.value &&
        showLastFixedColumnShadow.value &&
        'show-last-fixed-column-shadow',
      props.class,
    ]
      .filter(Boolean)
      .join(' '),
  );

  const {
    tableCustomHeight,
    itemsSlotsNames,
    onRowClick,
    rowIsClickable,
    rowsAreInteractive,
    onMiddleclickRow,
  } = useDataTableInternals({
    tableMinHeight: props.tableMinHeight,
    tableHeight: props.height,
    emits,
    headers: () => visibleHeaders.value,
    selectable: () => props.selectable,
    clickableRows: () => props.clickableRows === true,
  });

  useOnFiltersChange(
    () => props.filterNamespace,
    (filters) => emits('filter', { ...filters }),
  );
</script>

<template>
  <div :class="tableWithHeaderClass">
    <div
      v-if="!hideGeneralFilter"
      :class="[
        tableHeaderWrapperClass,
        tableTitle && filtersConfig?.length
          ? tableHeaderWrapperSpacingClass
          : undefined,
      ]"
    >
      <div :class="tableHeaderClass">
        <div v-if="tableTitle" :class="tableTitleClass">{{ tableTitle }}</div>
        <TableTopBar
          v-if="filterNamespace"
          :search-field-name="searchFieldName"
          :filter-namespace="filterNamespace"
          :search-field-placeholder="searchFieldPlaceholder"
          :filters-config="filtersConfig"
          :compact-filters="compactFilters"
          :hide-search="hideTextSearch"
          :hideable-columns="hideableColumns"
          :column-visibility-namespace="columnVisibilityNamespace"
          :non-hideable-columns="nonHideableColumns"
          :columns="headers"
          :export-action="exportAction"
        >
          <template #extra-filters-content
            ><slot name="extra-filters-content" /></template
          >
          <template #extra-action><slot name="extra-action" /></template>
          <template #additional-filters>
            <slot name="additional-filters"></slot>
          </template>
        </TableTopBar>
      </div>
    </div>
    <div ref="tableContainerRef" :class="tableContainerClass">
      <EasyDataTable
        :table-class-name="dataTableClasses"
        :headers="visibleHeaders"
        :items="items"
        :must-sort="mustSort"
        :alternating="alternating"
        :border-cell="borderCell"
        :show-index="showIndex"
        :fixed-checkbox="fixedCheckbox"
        :checkbox-column-width="checkboxColumnWidth"
        :rows-per-page="rowsPerPage"
        :fixed-index="fixedIndex"
        :index-column-width="indexColumnWidth"
        :table-min-height="tableMinHeight"
        :table-height="autoHeight ? null : tableCustomHeight"
        :theme-color="semanticTokenVars.surface.background.brand.default.subtle"
        :max-items-selectable="maxItemsSelectable"
        :loading="loading"
        :body-item-class-name="itemClassNameFunction(visibleHeaders)"
        :header-item-class-name="headerClassNameFunction"
        :body-row-class-name="
        rowClassNameFunction({
          rowIsClickable,
          customRowClassName: bodyRowClassName,
        })
      "
        :keyboard-activatable-rows="rowIsClickable && clickableRows"
        :disabled-reason="disabledReason"
        :sort-by="sortBy"
        :sort-type="sortType"
        :no-hover="!rowsAreInteractive"
        @click-row="onRowClick"
        @middleclick-row="onMiddleclickRow"
        hide-footer
        v-bind="attrs"
      >
        <template
          v-for="slot in itemsSlotsNames"
          :key="slot"
          #[slot]="slotProps"
        >
          <slot :name="slot" v-bind="slotProps" />
        </template>

        <template #loading>
          <slot name="loading">
            <div :class="centeredContainerClass"><Loader /></div>
          </slot>
        </template>

        <template #empty-message>
          <div :class="centeredContainerClass">
            <TableEmptyState
              v-if="filtersStore.isFilteringOrSearching.value"
              filtered
              :filter-namespace="filterNamespace"
              :persistent-filters="hasCompactFilters(filtersConfig, compactFilters)"
            />
            <slot v-else name="empty-message">
              <TableEmptyState :entity-name="entityName" />
            </slot>
          </div>
        </template>
      </EasyDataTable>
      <slot name="footer">
        <div
          :class="tableFooterClass"
          v-if="selectable && (attrs['items-selected'] as [])?.length"
        >
          <slot name="selection">
            <DataTableSelection
              v-if="!loading && selectable"
              data-test-id="items-selected"
              :entity-name="entityName"
              :items-selected="(attrs['items-selected'] as [])?.length"
              :max-items-selectable="maxItemsSelectable"
            />
          </slot>
        </div>
      </slot>
    </div>
  </div>
</template>

<style>
  @import "./DataTable-override.css";
</style>
